// DeployTimeline — visual timeline das fases retroativamente parseadas
// do output do deploy.sh (no result/error view do DeployModal).
//
// Espelha o look-and-feel do PhaseTimeline (HardRestart) mas baseado
// em ParsedDeployPhase em vez de PhaseEntry.

import { Box, Collapse, Group, Stack, Text, ThemeIcon, Timeline, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconBuildingFactory2,
  IconChevronRight,
  IconCircleCheck,
  IconCircleX,
  IconFlag,
  IconNetwork,
  IconStack2,
} from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { COLOR } from './tokens'
import type { ParsedDeployPhase } from './parseDeployOutput'

interface Props {
  phases: ParsedDeployPhase[]
}

function formatMs(ms?: number): string | null {
  if (ms == null) return null
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60_000)
  const s = Math.floor((ms % 60_000) / 1000)
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

function pickIcon(name: string): React.ComponentType<any> {
  if (name.startsWith('Building')) return IconBuildingFactory2
  if (name === 'Networking') return IconNetwork
  if (name === 'Containers') return IconStack2
  if (name === 'Concluído') return IconFlag
  return IconCircleCheck
}

export function DeployTimeline({ phases }: Props) {
  if (phases.length === 0) return null

  const lastIdx = phases.length - 1
  return (
    <Timeline
      active={lastIdx}
      bulletSize={28}
      lineWidth={2}
      role="list"
      aria-label="Etapas do deploy.sh"
      styles={{
        itemTitle: { fontWeight: 600, fontFamily: '"Barlow", sans-serif' },
      }}
    >
      {phases.map((phase, i) => (
        <Timeline.Item
          key={`${phase.name}-${i}`}
          bullet={<DeployBullet phase={phase} />}
          title={
            <Group gap={8} justify="space-between" wrap="nowrap">
              <Text size="sm" fw={600} role="listitem">
                {phase.name}
              </Text>
              <Group gap={6} wrap="nowrap">
                {phase.durationMs != null && (
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    {formatMs(phase.durationMs)}
                  </Text>
                )}
              </Group>
            </Group>
          }
        >
          <DeployPhaseDetails phase={phase} />
        </Timeline.Item>
      ))}
    </Timeline>
  )
}

function DeployBullet({ phase }: { phase: ParsedDeployPhase }) {
  if (phase.status === 'failed') {
    return (
      <ThemeIcon size={28} radius="xl" color="red" variant="filled">
        <IconCircleX size={16} />
      </ThemeIcon>
    )
  }
  if (phase.status === 'success') {
    return (
      <ThemeIcon size={28} radius="xl" color="inovareTeal" variant="filled">
        <IconCircleCheck size={16} />
      </ThemeIcon>
    )
  }
  // pending / running — fallback (parser typically only produces terminal states)
  const Icon = pickIcon(phase.name)
  return (
    <ThemeIcon size={28} radius="xl" color="gray" variant="light">
      <Icon size={16} />
    </ThemeIcon>
  )
}

function DeployPhaseDetails({ phase }: { phase: ParsedDeployPhase }) {
  const [opened, { toggle }] = useDisclosure(false)
  const hasDetails = (phase.details?.length ?? 0) > 0

  return (
    <Stack gap={4}>
      {phase.summary && (
        <Text size="xs" c="dimmed" pl={4}>
          {phase.summary}
        </Text>
      )}
      {hasDetails && (
        <>
          <UnstyledButton
            onClick={toggle}
            aria-expanded={opened}
            aria-label={opened ? 'Recolher detalhes' : 'Expandir detalhes'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px 4px 4px',
              borderRadius: 6,
              transition: 'background 120ms ease',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(2, 84, 104, 0.06)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <motion.span
              animate={{ rotate: opened ? 90 : 0 }}
              transition={{ duration: 0.18 }}
              style={{ display: 'inline-flex', color: 'var(--ai-text-secondary, #5f7882)' }}
            >
              <IconChevronRight size={14} />
            </motion.span>
            <Text size="sm" c="dimmed" fw={500}>
              {opened ? 'Ocultar detalhes' : `Ver ${phase.details!.length} etapas`}
            </Text>
          </UnstyledButton>
          <Collapse in={opened}>
            <Box
              component="pre"
              mt={4}
              p={10}
              style={{
                background: COLOR.surfaceDark,
                color: '#c9d1d9',
                borderRadius: 6,
                fontSize: 11,
                fontFamily: '"JetBrains Mono", monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: 200,
                overflowY: 'auto',
                margin: 0,
              }}
            >
              {phase.details!.join('\n')}
            </Box>
          </Collapse>
        </>
      )}
    </Stack>
  )
}
